import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { errorMessage } from '@/features/auth/use-auth-error';
import { trackPageview } from '@/lib/analytics';

import { AppShell } from '../app-shell';
import { useLookups, useMe } from '../queries';
import {
  ANALYTICS_STEP,
  PROGRESS_STEPS,
  destinationPath,
  isStep,
  progressIndex,
  stepFromRecord,
  type Destination,
} from '../steps';
import { AvailabilityStep } from '../steps/availability-step';
import { ContactStep } from '../steps/contact-step';
import { ProfileStep } from '../steps/profile-step';
import { WelcomeStep } from '../steps/welcome-step';
import { WorkBackgroundStep } from '../steps/work-background-step';
import { WorkTypeStep } from '../steps/work-type-step';

/** `/apply`: work out where the applicant is and send them there. */
export function ApplyIndexPage() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (me.isError) return <LoadError error={me.error} retry={() => void me.refetch()} />;
  return <Navigate to={destinationPath(stepFromRecord(me.data))} replace />;
}

/** `/apply/:step`: render one step against the cached record. */
export function ApplyStepPage() {
  const { step = '' } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const lookups = useLookups();

  useEffect(() => {
    if (isStep(step)) trackPageview(ANALYTICS_STEP[step]);
  }, [step]);

  if (!isStep(step)) return <Navigate to="/404" replace />;
  const current = step;

  if (me.isPending || lookups.isPending) {
    return (
      <AppShell step={current}>
        <Loading />
      </AppShell>
    );
  }
  if (me.isError) return <LoadError error={me.error} retry={() => void me.refetch()} />;
  if (lookups.isError)
    return <LoadError error={lookups.error} retry={() => void lookups.refetch()} />;
  if (me.data.hasRecentApplication === false)
    return <Navigate to="/no-recent-application" replace />;

  const onNext = (to: Destination) => void navigate(destinationPath(to));
  const props = { record: me.data, lookups: lookups.data, onNext };
  const idx = progressIndex(current);
  const previous = idx > 0 ? PROGRESS_STEPS[idx - 1] : undefined;

  return (
    <AppShell step={current}>
      {previous && (
        <button
          type="button"
          className="back-link"
          onClick={() => void navigate(`/apply/${previous.step}`)}
        >
          ← {previous.label}
        </button>
      )}
      {current === 'welcome' && <WelcomeStep {...props} />}
      {current === 'profile' && <ProfileStep {...props} />}
      {current === 'work_type' && <WorkTypeStep {...props} />}
      {current === 'work_background' && <WorkBackgroundStep {...props} />}
      {current === 'availability' && <AvailabilityStep {...props} />}
      {current === 'contact' && <ContactStep {...props} variant="eligible" />}
      {current === 'contact_waiting' && <ContactStep {...props} variant="waiting" />}
    </AppShell>
  );
}

function Loading() {
  return (
    <p className="muted" role="status" style={{ padding: '2rem' }}>
      Loading…
    </p>
  );
}

function LoadError({ error, retry }: { error: unknown; retry: () => void }) {
  return (
    <div className="stack" style={{ padding: '2rem', maxWidth: '32rem' }}>
      <Alert>{errorMessage(error)}</Alert>
      <Button variant="secondary" onClick={retry}>
        Try again
      </Button>
    </div>
  );
}
